import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Box, Typography, Paper, Grid, Chip, Button,
  Alert, IconButton, Tooltip, Divider, CircularProgress,
  TextField, MenuItem, Select, FormControl, InputLabel, Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PrintIcon from '@mui/icons-material/Print';
import Visibility from '@mui/icons-material/Visibility';
import QRCodeDialog from '../components/QRCodeDialog';
import GenerateQRDialog from '../components/GenerateQRDialog';
import { alpha, useTheme } from '@mui/material/styles';

function QRCodes() {
  const theme = useTheme();
  const [qrCodes, setQrCodes] = useState([]);
  const [allResidences, setAllResidences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [resFilter, setResFilter] = useState('Toutes');
  const [typeFilter, setTypeFilter] = useState('Tous');
  const [selectedQR, setSelectedQR] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  // Charger les résidences
  const loadResidences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/residences', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllResidences(data.residences || []);
      }
    } catch (e) {
      console.error('Erreur chargement résidences:', e);
    }
  };

  // Normalisation des QR codes
  const normalizeList = (raw) => {
    const listRaw = Array.isArray(raw?.qrCodes)
      ? raw.qrCodes
      : Array.isArray(raw?.qrcodes)
      ? raw.qrcodes
      : Array.isArray(raw)
      ? raw
      : [];
    return listRaw.map(q => ({
      id: q.id,
      code: q.code ?? '—',
      description: q.description ?? q.label ?? '',
      residenceId: q.residenceId ?? null,
      residenceName: q.residenceName ?? '—',
      location: q.location ?? '—',
      locationType: q.locationType ?? '—',
      scanCount: typeof q.scanCount === 'number' ? q.scanCount : 0,
      isActive: typeof q.isActive === 'boolean' ? q.isActive : true,
      createdAt: q.createdAt ?? null,
      updatedAt: q.updatedAt ?? null,
    }));
  };

  // Charger les QR codes
  const loadQRCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const doFetch = async (url) => {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      };
      
      let data;
      try {
        data = await doFetch('/api/qrcodes');
      } catch {
        data = await doFetch('/api/qr-codes');
      }
      
      const list = normalizeList(data);
      setQrCodes(list);
    } catch (e) {
      console.error('QR Codes load error:', e);
      setError("Erreur lors du chargement des données.");
      setQrCodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResidences();
    loadQRCodes();
  }, []);

  // Types de location uniques
  const locationTypes = useMemo(() => {
    const uniq = new Set();
    qrCodes.forEach(q => {
      if (q.locationType && q.locationType !== '—') uniq.add(q.locationType);
    });
    return ['Tous', ...Array.from(uniq)];
  }, [qrCodes]);

  // Filtrage
  const filtered = useMemo(() => {
    return qrCodes.filter(q => {
      const okSearch = !search ||
        q.code.toLowerCase().includes(search.toLowerCase()) ||
        q.description.toLowerCase().includes(search.toLowerCase()) ||
        q.location.toLowerCase().includes(search.toLowerCase());
      
      const okRes = resFilter === 'Toutes' || 
        q.residenceName === resFilter ||
        (resFilter === 'Sans résidence' && (!q.residenceName || q.residenceName === '—'));
      
      const okType = typeFilter === 'Tous' || q.locationType === typeFilter;
      
      return okSearch && okRes && okType;
    });
  }, [qrCodes, search, resFilter, typeFilter]);

  const handleQRClick = (qr) => {
    setSelectedQR(qr);
    setShowQRDialog(true);
  };

  const handlePrint = (qr) => {
    setSelectedQR(qr);
    setShowQRDialog(true);
  };

  const handleGenerateQR = () => {
    setShowGenerateDialog(true);
  };

  const handleQRGenerated = (newQR) => {
    loadQRCodes();
    if (newQR) {
      setSelectedQR(newQR);
      setShowQRDialog(true);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Gestion des QR Codes
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher (code, description, résidence, emplacement)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: '🔍'
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Résidence</InputLabel>
              <Select
                value={resFilter}
                onChange={(e) => setResFilter(e.target.value)}
                label="Résidence"
              >
                <MenuItem value="Toutes">Toutes</MenuItem>
                {allResidences.map(res => (
                  <MenuItem key={res.id} value={res.name}>
                    {res.name}
                  </MenuItem>
                ))}
                <MenuItem value="Sans résidence">
                  <em>Sans résidence</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                {locationTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={loadQRCodes} color="primary">
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleGenerateQR}
                fullWidth
              >
                Générer QR Code
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <QrCodeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary">
            {qrCodes.length === 0 
              ? "Aucun QR Code créé pour le moment"
              : "Aucun QR Code ne correspond aux filtres"}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((qr) => (
            <Grid item xs={12} md={6} lg={4} key={qr.id}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleQRClick(qr)}
                        sx={{ p: 0.5 }}
                      >
                        <QrCodeIcon />
                      </IconButton>
                      <Typography variant="h6" fontWeight="bold">
                        {qr.code}
                      </Typography>
                    </Stack>
                    <Chip 
                      label={qr.isActive ? 'Actif' : 'Inactif'} 
                      color={qr.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>

                  <Typography color="text.secondary" variant="body2">
                    {qr.description || 'Sans description'}
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ApartmentIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Résidence: {qr.residenceName}
                      </Typography>
                    </Stack>
                    
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Emplacement: {qr.location}
                      </Typography>
                    </Stack>
                    
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Type: {qr.locationType}
                      </Typography>
                    </Stack>
                    
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Scans: {qr.scanCount}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1}>
  		    <IconButton
		      size="small"
		      color="primary"
		      onClick={() => handleQRClick(qr)}
		      sx={{ 
			border: '1px solid',
		        borderColor: 'primary.main',
		        '&:hover': {
		          bgcolor: 'primary.light',
		          bgcolor: alpha(theme.palette.primary.main, 0.1)
			}
		      }}
		    >
		      <Visibility />
		    </IconButton>
		    <Button
		      size="small"
		      variant="outlined"
		      onClick={() => window.open(`/scan/${qr.code}`, '_blank')}
		    >
		      Tester
		    </Button>
		    <Button
		      size="small"
		      variant="outlined"
		      startIcon={<PrintIcon />}
		      onClick={() => handlePrint(qr)}
		    >
		      Imprimer
		    </Button>
		  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <QRCodeDialog
        open={showQRDialog}
        onClose={() => setShowQRDialog(false)}
        qrData={selectedQR}
      />

      <GenerateQRDialog
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onSuccess={handleQRGenerated}
      />
    </Container>
  );
}

export default QRCodes;
